import { useState, type ReactNode } from 'react';
import { beforeWords } from '../period/compare';
import { Card, CardHeader, CardTitle, Funnel, type FunnelBase, type FunnelStep } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { RangeSwitch } from '../insights/RangeSwitch';
import type { Funnel as FunnelView } from './funnel-types';
import { medianActivationSeconds, stepGaps, type StepGap } from './funnel-math';
import { stepHref, stoppedHref } from './funnel-links';
import {
  activatedHelp,
  exportMedianWord,
  funnelStepWord,
  gapMedianWord,
  paidHelp,
  setupWord,
} from './funnel-words';
import './funnel.css';

const BASES = [
  { value: 'first' as const, label: 'Share of first' },
  { value: 'previous' as const, label: 'Share of previous step' },
];

interface GapProps {
  gap: StepGap;
  funnel: FunnelView;
  f: Formatter;
  renderLink: (href: string, children: ReactNode) => ReactNode;
}

function wentOnWord(gap: StepGap, f: Formatter): string {
  if (gap.conversion === null) return 'nobody to convert';
  const went = Math.min(gap.reached, gap.entered);
  return `${f.int(went)} of ${f.int(gap.entered)} went on (${gap.conversion}%)`;
}

function medianOf({ gap, funnel }: Pick<GapProps, 'gap' | 'funnel'>): string | null {
  if (gap.to !== 'firstExport') {
    return gapMedianWord(gap.from, Math.min(gap.reached, gap.entered), gap.medianSeconds);
  }
  return exportMedianWord(gap.from, gap.medianSeconds, medianActivationSeconds(funnel.companies));
}

/** Between two rows: how many went on, who dropped off (a link to them), and how long it took. */
function GapLine(props: GapProps): ReactNode {
  const { gap, funnel, f, renderLink } = props;
  const href = stoppedHref(funnel, gap.from);
  const dropped = `${f.int(gap.dropped)} dropped off`;
  const median = medianOf(props);
  return (
    <span className="ad-funnel-gap">
      <span aria-hidden="true">↓ </span>
      {wentOnWord(gap, f)}
      {gap.dropped > 0 ? <> · {href ? renderLink(href, dropped) : dropped}</> : null}
      {median ? <> · {median}</> : null}
    </span>
  );
}

/** The steps as the funnel draws them, each name a link to its companies. */
function stepsOf(funnel: FunnelView): FunnelStep[] {
  return funnel.steps.map((s) => {
    const href = stepHref(funnel, s.step);
    return {
      label: funnelStepWord(s.step),
      value: s.count,
      previous: s.previous,
      ...(href ? { href } : {}),
    };
  });
}

/** The two marks named: the bar is this period, the outline the one before. */
function Legend({ before }: { before: string }): ReactNode {
  return (
    <p className="ad-small ad-funnel-legend">
      <span className="ad-funnel-legend__bar" aria-hidden="true" /> this period{' '}
      <span className="ad-funnel-legend__ghost" aria-hidden="true" /> {before}. {activatedHelp}{' '}
      {paidHelp} Each step name opens its companies.
    </p>
  );
}

export interface FunnelStepsProps {
  funnel: FunnelView;
  renderLink: (href: string, children: ReactNode) => ReactNode;
}

/**
 * The funnel and the step-to-step table in one (round 3, dataviz M4): a row per step with its bar,
 * count and share, the period before as an outline, and between rows the conversion, the drop-off
 * (a link to those companies) and the median time from the step before. The bars measure against
 * the first step or the step before, so the late steps stay readable at real conversion.
 */
export function FunnelSteps({ funnel, renderLink }: FunnelStepsProps): ReactNode {
  const f = useFormat();
  const [base, setBase] = useState<FunnelBase>('first');
  const gaps = stepGaps(funnel.steps);
  const anyone = funnel.steps.some((s) => s.count > 0 || s.previous > 0);
  // The period the API answered for; an older server says only how many weeks.
  const before = funnel.period
    ? beforeWords(funnel.period.days)
    : `the ${funnel.weeks} weeks before`;
  return (
    <Card className="ad-funnel__chart">
      <CardHeader>
        <CardTitle>This period against {before}</CardTitle>
        <RangeSwitch label="Bar length" options={BASES} value={base} onChange={setBase} />
      </CardHeader>
      <Legend before={before} />
      {anyone ? (
        <Funnel
          steps={stepsOf(funnel)}
          label="Signed up to paid, this period against the one before; each step opens its companies"
          renderLink={renderLink}
          format={f.int}
          base={base}
          previousLabel={before}
          between={(i) => (
            <GapLine gap={gaps[i - 1]!} funnel={funnel} f={f} renderLink={renderLink} />
          )}
        />
      ) : (
        <p className="ad-small">Nobody signed up in this period or the one before.</p>
      )}
      {anyone ? (
        <p className="ad-small ad-funnel-setup">
          {setupWord(funnel.setupFinished.count, funnel.setupFinished.previous)}
        </p>
      ) : null}
    </Card>
  );
}
